import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import {
  VaultManagedTypeSelect,
  type VaultManagedType,
} from '@/components/vaults/vaultManagedTypeSelect';
import type { StepProps } from '@/components/wizard/StepProps';
import { validateName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { joinByCommaWithLastAnd } from '@/utils/string';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { AwsByovSupportedResourceTypes } from './awsByovSupportedResourceTypes';
import { HighFrequencySupportedResourceTypes } from './highFrequencySupportedResourceTypes';
import { pitrPolicySupportedResourceTypes } from './pitrPolicySupportedResourceTypes';

export const getPolicySupportedResourceTypes = (
  policyType: BackupPolicyType,
  vaultManagedType?: VaultManagedType
) => {
  const policyTypeSupportedResourceTypes =
    policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
      ? HighFrequencySupportedResourceTypes
      : policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
        ? pitrPolicySupportedResourceTypes
        : undefined;

  const vaultManagedTypeSupportedResourceTypes =
    vaultManagedType === 'self-managed-AWS'
      ? AwsByovSupportedResourceTypes
      : undefined;

  if (
    !policyTypeSupportedResourceTypes &&
    !vaultManagedTypeSupportedResourceTypes
  ) {
    return undefined;
  }

  if (
    policyTypeSupportedResourceTypes &&
    vaultManagedTypeSupportedResourceTypes
  ) {
    return policyTypeSupportedResourceTypes?.filter((type) =>
      vaultManagedTypeSupportedResourceTypes?.includes(type)
    );
  }

  if (policyTypeSupportedResourceTypes) {
    return policyTypeSupportedResourceTypes;
  }

  if (vaultManagedTypeSupportedResourceTypes) {
    return vaultManagedTypeSupportedResourceTypes;
  }
};

export const BackupPolicyNameSelection = (
  props: StepProps<BackupPolicyEditState>
) => {
  const next = () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='40px'>
        <PolicyNameInput {...props} />
        <PolicyTypeSelection
          selectedType={props.wizardState.policyType}
          setSelectedType={(type) => {
            const resourceTypes = getPolicySupportedResourceTypes(
              type,
              props.wizardState.vaultManagedType
            );
            props.setWizardState({
              ...props.wizardState,
              policyType: type,
              resourceTypes,
            });
          }}
        />
        <VaultTypeSelection {...props} />
        {props.wizardState.resourceTypes && (
          <>
            {props.wizardState.resourceTypes.length ? (
              <Typography variant='body2'>
                Available for{' '}
                {joinByCommaWithLastAnd(
                  props.wizardState.resourceTypes.map(
                    (x) => ResourceTypes[x].displayName
                  )
                )}
                .
              </Typography>
            ) : (
              <Typography variant='body2'>Currently not supported.</Typography>
            )}
          </>
        )}
        <Button
          data-testid='create-backup-policy-name-continue-btn'
          variant='contained'
          disabled={
            !validateName(props.wizardState.name) ||
            (props.wizardState.resourceTypes &&
              !props.wizardState.resourceTypes.length)
          }
          onClick={next}
          sx={{ height: '48px' }}
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  );
};

const VaultTypeSelection = (props: StepProps<BackupPolicyEditState>) => {
  const dal = useDAL();
  const { data: vaultAccounts } = dal.vaults.getAccounts();
  const selfManagedVaultAccounts =
    vaultAccounts?.accounts.filter((account) => account.isCustomerOwned) || [];
  const eonManagedVaultAccounts =
    vaultAccounts?.accounts.filter((account) => !account.isCustomerOwned) || [];
  const selfManagedCloudProviders = Array.from(
    new Set(selfManagedVaultAccounts.map((a) => a.cloudProvider))
  );
  const shouldShowVaultTypeSelect =
    (!!selfManagedVaultAccounts.length && !!eonManagedVaultAccounts.length) ||
    selfManagedCloudProviders.length > 1;

  if (!shouldShowVaultTypeSelect) {
    return null;
  }

  return (
    <VaultManagedTypeSelect
      title='Save snapshots in'
      selfManagedVaultAccounts={selfManagedVaultAccounts}
      showEonManaged={!!eonManagedVaultAccounts.length}
      restrictedCloudProviders={selfManagedCloudProviders}
      regionRequired={false}
      vaultManagedType={props.wizardState.vaultManagedType}
      onChange={(vaultManagedType) => {
        props.setWizardState({
          ...props.wizardState,
          vaultManagedType,
          resourceTypes: getPolicySupportedResourceTypes(
            props.wizardState.policyType,
            vaultManagedType
          ),
        });
      }}
    />
  );
};

const PolicyNameInput = (props: StepProps<BackupPolicyEditState>) => {
  return (
    <Stack gap='16px'>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Name</FormLabel>
        <OutlinedInput
          inputProps={{ 'data-testid': 'create-backup-policy-name-input' }}
          autoFocus
          sx={{
            padding: '8px',
          }}
          placeholder='Use a descriptive name, such as "Production with PII"'
          onChange={(e) =>
            props.setWizardState({
              ...props.wizardState,
              name: e.target.value,
            })
          }
          value={props.wizardState.name}
        />
      </FormControl>
    </Stack>
  );
};

const usePolicyTypeLabels = (): Partial<
  Record<BackupPolicyType, { label: string; description: string }>
> => {
  const { pitrEnabled, enableHourlyBackup } = useFeatureFlags();

  return {
    [BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD]: {
      label: 'Standard',
      description: 'Backup runs up to 4 times a day',
    },
    ...(!enableHourlyBackup
      ? {
          [BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY]: {
            label: 'High frequency',
            description: `Hourly backups supported.`,
          },
        }
      : {}),
    ...(pitrEnabled
      ? {
          [BackupPolicyType.BACKUP_POLICY_TYPE_PITR]: {
            label: 'Point-in-time recovery',
            description: `PiTR backups supported.`,
          },
        }
      : {}),
  };
};

const PolicyTypeSelection = ({
  selectedType,
  setSelectedType,
}: {
  selectedType: BackupPolicyType;
  setSelectedType: (type: BackupPolicyType) => void;
}) => {
  const policyTypeLabels = usePolicyTypeLabels();
  return (
    <Stack gap='16px'>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Type</FormLabel>
        <Select
          sx={{ height: '48px', paddingLeft: '8px' }}
          size='small'
          value={selectedType || BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD}
          onChange={(event) =>
            setSelectedType(event.target.value as BackupPolicyType)
          }
          displayEmpty
          renderValue={(value: BackupPolicyType) => {
            return policyTypeLabels[value]?.label;
          }}
        >
          {Object.entries(policyTypeLabels).map(([key, value]) => (
            <MenuItem key={key} value={key}>
              <Stack gap='4px' padding='4px'>
                <Typography variant='body1'>{value.label}</Typography>
                <Typography variant='subtitle2' color='text.secondary'>
                  {value.description}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};
